import { styled } from '@mui/material/styles';
import { BRAND } from './theme/paletteDark';
import { FONTS } from './theme/typography';
import { RAIL_BREAKPOINT, TAG_RAIL_W } from '../features/explore/EventList';
import { PHONE_GUTTER } from '../features/explore/EventTicket';

/**
 * Everything but the view toggle.
 *
 * One element so that the account page, which is drawn over the app rather than
 * in place of it, can make the whole app inert in a single place — and so that
 * the toggle, which stays live over it, can sit outside.
 */
export const Content = styled('div')({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export const Shell = styled('div')({
  height: '100dvh',
  display: 'flex',
  flexDirection: 'column',
  background: BRAND.mocha,
  color: BRAND.parchment,
});

/**
 * Below 900px, the bar across the top.
 *
 * Its two columns are a prop rather than a media query, because the two devices
 * that share a width want opposite things and no query can tell them apart:
 *
 *   `auto 1fr` — a desktop window under 900px. The lockup shrinks to fit and
 *     the view toggle takes the rest of the row, centred in it (§4.3).
 *   `1fr auto` — a phone, whose toggle has gone to the bottom of the screen.
 *     The lockup takes the room and Saved, or Share, sits at the end.
 *
 * `$below` is the room a phone's "Showing N of M events" hangs into, under the
 * Saved pill: it is drawn out of flow so that the pill stays centred on the
 * lockup, and this is the header's way of reserving its space.
 */
export const Header = styled('header', {
  shouldForwardProp: (p) => p !== '$template' && p !== '$below',
})<{ $template?: string; $below?: number }>(({ theme, $template, $below = 0 }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: 16,
  padding: '14px 26px',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: $template ?? 'auto 1fr',
    padding: `11px 16px ${11 + $below}px`,
    gap: 10,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: $template ?? 'auto 1fr',
    padding: `10px 12px ${10 + $below}px`,
    gap: 14,
  },
}));

/**
 * The header's right-hand cell on a phone, where the toggle used to be: Saved
 * and "Showing…" on Explore, Share My Schedule on My Schedule. The two are
 * stacked rather than swapped, so the header is one height in both views and
 * the pair can cross-fade with the page swipe.
 */
/**
 * A desktop window under 900px: the toggle takes the row to the right of the
 * lockup and is centred in it. The negative margins give the cell back the
 * header's own column gap and right padding, so "centred" is measured from the
 * lockup's right edge to the screen's edge, which is what the eye reads, and
 * not from the grid cell's.
 */
export const HeaderToggle = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  minWidth: 0,
  [theme.breakpoints.down('md')]: { marginLeft: -10, marginRight: -16 },
  [theme.breakpoints.down('sm')]: { marginLeft: -14, marginRight: -12 },
}));

export const HeaderRight = styled('div')({
  position: 'relative',
  justifySelf: 'end',
  minWidth: 0,
  display: 'flex',
  justifyContent: 'flex-end',
});

export const Panes = styled('div')(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  display: 'flex',
  alignItems: 'stretch',
  // From 900px up the columns run to the page's edges.
  padding: 0,
  gap: 0,
  [theme.breakpoints.down('md')]: { padding: '0 14px' },
  // A phone's list runs edge to edge: its cards are swiped off the screen.
  [theme.breakpoints.down('sm')]: { padding: 0 },
}));

/** Each pane scrolls on its own, so filtering never moves the list. */
export const Pane = styled('div')({
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  paddingLeft: 22,
  // Reserve room for the scrollbar so it can never sit over the card's three
  // action buttons at narrow widths.
  paddingRight: 18,
  scrollbarGutter: 'stable',
  // Below 900px a sideways swipe on the list is the page's: filters one way,
  // My Schedule the other. This is the scroll container, so it has to say so itself.
  '@media (max-width: 899.95px)': { touchAction: 'pan-y' },
  // The ticket centred between equal gutters. A card on its way off the screen
  // is clipped at the edge rather than scrolling the list sideways.
  '@media (max-width: 599px)': {
    paddingLeft: PHONE_GUTTER,
    paddingRight: PHONE_GUTTER,
    overflowX: 'hidden',
  },
});

/**
 * The list is centered within whatever width the sidebar leaves. On a 2000px
 * page with a 666px sidebar the cards' midpoint lands at 1333px, not at 1000px.
 */
export const Centered = styled('div')({
  width: '100%',
  maxWidth: 940,
  marginLeft: 'auto',
  marginRight: 'auto',
});

/** Wraps the scrolling pane so the fades can sit above it without scrolling. */
export const PaneWrap = styled('div')({
  position: 'relative',
  flex: 1,
  minWidth: 0,
  minHeight: 0,
  display: 'flex',
});

/**
 * Cards fade into the page chrome rather than being clipped by a hard edge.
 * The top fade is opaque behind the sticky results bar and the pinned date, then
 * falls away; the bottom is a shorter fade back in. Both are inert to the
 * pointer so they never intercept a click on a card underneath.
 */
export const Fade = styled('div', { shouldForwardProp: (p) => p !== '$edge' && p !== '$phone' })<{
  $edge: 'top' | 'bottom';
  /** The phone chrome, where both edges are redrawn to the Sept 19 reference. */
  $phone?: boolean;
}>(({ $edge, $phone }) => ({
  position: 'absolute',
  left: 0,
  right: 0,
  pointerEvents: 'none',
  zIndex: 5,
  ...($edge === 'top'
    ? $phone
      ? {
          // No results bar above it any more: the fade is only the ground
          // behind the held date. Opaque through the date itself, about a third
          // strength where the first card's top edge sits (64.5px down, the
          // held date's own height) and gone 13px below that.
          top: 0,
          height: 77.5,
          background: `linear-gradient(to bottom, ${BRAND.mocha} 0%, ${BRAND.mocha} 65%, rgba(54,47,42,0.3) 83%, rgba(54,47,42,0) 100%)`,
        }
      : {
          top: 0,
          height: 132,
          background: `linear-gradient(to bottom, ${BRAND.mocha} 0%, ${BRAND.mocha} 34%, rgba(54,47,42,0.82) 58%, rgba(54,47,42,0) 100%)`,
        }
    : $phone
      ? {
          // The scrim under the toggle: solid for the bottom 20.6px of 59.3,
          // then away. Never solid mocha — the cards read through it.
          bottom: 0,
          height: 59.3,
          background:
            'linear-gradient(to top, rgba(54,47,42,0.567) 0%, rgba(54,47,42,0.567) 34.7%, rgba(54,47,42,0) 100%)',
        }
      : {
          bottom: 0,
          height: 84,
          background: `linear-gradient(to top, ${BRAND.mocha} 0%, rgba(54,47,42,0.7) 45%, rgba(54,47,42,0) 100%)`,
        }),
}));

export const TextLink = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontFamily: FONTS.display,
  fontSize: 14,
  fontWeight: 700,
  color: BRAND.parchment,
  '&:hover': { opacity: 0.75 },
});

export const ResultsBar = styled('div')({
  position: 'sticky',
  top: 0,
  // Above the date header (6), which is itself above the top fade (5).
  zIndex: 7,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '8px 0 6px',
  // Left edge tracks the card, matching the date header below it.
  paddingLeft: TAG_RAIL_W,
  background: BRAND.mocha,
  // Right edge tracks the ticket too: the card row is capped at 880 and ends in
  // the 30px action column plus its 8px margin, which sit outside the ticket.
  maxWidth: 880,
  paddingRight: 38,
  [`@media (max-width: ${RAIL_BREAKPOINT - 1}px)`]: { paddingLeft: 0 },
  // The filters' toggle sits in the list's top-left corner from 900px up, and
  // without the rail to clear it the title would start underneath it.
  [`@media (min-width: 900px) and (max-width: ${RAIL_BREAKPOINT - 1}px)`]: { paddingLeft: 30 },
  /**
   * A desktop window under 600px keeps the bar in the list (a phone's has moved
   * into the header), but there is no room for its two items side by side: they
   * stack at the right, as in Sam's 514px mock.
   */
  '@media (max-width: 599px)': {
    maxWidth: 'none',
    paddingRight: 0,
    // Reversed, so Saved keeps the first row and the count sits under it
    // without the two swapping places in the reading order.
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
    gap: 4,
  },
});

/**
 * Off: an outlined pill. On: a filled parchment pill with a thin mocha gap
 * inside its outline, and the icons filled.
 */
export const SavedPill = styled('button', { shouldForwardProp: (p) => p !== '$on' && p !== '$phone' })<{
  $on: boolean;
  /** In a phone's header rather than in the list: smaller, to the Sept 19 reference. */
  $phone?: boolean;
}>(({ $on, $phone }) => ({
  appearance: 'none',
  cursor: 'pointer',
  flexShrink: 0,
  display: 'inline-flex',
  alignItems: 'center',
  gap: $phone ? 3.5 : 5,
  padding: $phone ? '0 15px 0 12px' : '5px 16px',
  ...($phone ? { height: 30, boxSizing: 'border-box' as const } : null),
  borderRadius: 999,
  fontFamily: FONTS.display,
  fontSize: $phone ? 12 : 15,
  fontWeight: 700,
  lineHeight: 1.2,
  background: $on ? BRAND.parchment : 'transparent',
  color: $on ? BRAND.mocha : BRAND.parchment,
  border: `1px solid ${BRAND.parchment}`,
  boxShadow: $on ? `inset 0 0 0 1.5px ${BRAND.mocha}` : 'none',
  '&:hover': { background: $on ? BRAND.parchment : 'rgba(243,238,231,0.08)' },
}));

/** What "Showing…" hangs below the pill: its 8px gap and its 11px line. */
export const PHONE_HEADER_BELOW = 19;

/**
 * "Showing 1010 of 1010 events", under a phone's Saved pill. Out of flow, so
 * the pill above it stays centred on the lockup; the header reserves its room
 * with `$below` instead.
 */
export const ShowingLine = styled('div')({
  position: 'absolute',
  top: '100%',
  right: 3,
  marginTop: 8,
  fontFamily: FONTS.body,
  fontWeight: 400,
  fontSize: 11,
  lineHeight: 1,
  color: 'rgba(243,238,231,0.6)',
  whiteSpace: 'nowrap',
  pointerEvents: 'none',
});
